import React from 'react';
import { Trophy, Gift, Star, Coins } from 'lucide-react';
import { BattlePassReward, UserStats } from '../types';

interface BattlePassProps {
  rewards: BattlePassReward[];
  userStats: UserStats;
  onClaimReward?: (rewardId: string) => void;
}

const BattlePass: React.FC<BattlePassProps> = ({ rewards, userStats, onClaimReward }) => {
  // Group rewards by level
  const rewardsByLevel = rewards.reduce((acc, reward) => {
    if (!acc[reward.level]) acc[reward.level] = [];
    acc[reward.level].push(reward);
    return acc;
  }, {} as Record<number, BattlePassReward[]>);

  // Calculate progress percentage
  const progressPercentage = (userStats.xp / userStats.xpToNextLevel) * 100;
  
  return (
    <div className="bg-white rounded-xl shadow-lg p-4 w-full max-h-full overflow-y-auto">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold text-gray-800">Battle Pass</h2>
        <div className="flex items-center bg-gray-100 px-3 py-1 rounded-full">
          <Coins className="text-yellow-500 mr-1" size={16} />
          <span className="font-medium">{userStats.coins}</span>
        </div>
      </div>
      
      {/* Level Progress */}
      <div className="mb-6">
        <div className="flex justify-between mb-1 text-sm">
          <span>Level {userStats.level}</span>
          <span>{userStats.xp}/{userStats.xpToNextLevel} XP</span>
        </div>
        <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
          <div 
            className="h-full bg-gradient-to-r from-blue-400 to-purple-500" 
            style={{ width: `${progressPercentage}%` }}
          ></div>
        </div>
      </div>
      
      {/* Battle Pass Rewards */}
      <div className="space-y-6">
        {Array.from({ length: 30 }, (_, i) => i + 1).map(level => (
          <div key={level} className="relative">
            {/* Level indicator */}
            <div className="flex items-center mb-3">
              <div className={`
                w-8 h-8 rounded-full flex items-center justify-center
                ${level <= userStats.level ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-500'}
              `}>
                {level}
              </div>
              <div className="ml-2 flex-1 h-0.5 bg-gray-200"></div>
              {level <= userStats.level && (
                <Star className="w-5 h-5 text-yellow-400" />
              )}
            </div>
            
            {/* Rewards for this level */}
            {rewardsByLevel[level] && (
              <div className="grid grid-cols-2 gap-3 ml-8">
                {rewardsByLevel[level].map(reward => (
                  <div 
                    key={reward.id}
                    className={`
                      relative rounded-lg p-3 border-2 
                      ${reward.claimed 
                        ? 'border-gray-200 bg-gray-50 opacity-60' 
                        : level <= userStats.level
                          ? 'border-blue-400 bg-blue-50'
                          : 'border-gray-200 bg-gray-50 opacity-60'
                      }
                    `}
                  >
                    <div className="flex items-center">
                      {reward.type === 'coins' ? (
                        <div className="w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center">
                          <Coins className="w-6 h-6 text-yellow-500" />
                        </div>
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                          <Gift className="w-6 h-6 text-purple-500" />
                        </div>
                      )}
                      <div className="ml-2">
                        <h4 className="font-medium text-sm">{reward.title}</h4>
                        {reward.brand && (
                          <span className="text-xs text-gray-500">{reward.brand}</span>
                        )}
                      </div>
                    </div>
                    
                    {level <= userStats.level && !reward.claimed && onClaimReward && (
                      <button
                        onClick={() => onClaimReward(reward.id)}
                        className="mt-2 w-full py-1 px-3 bg-blue-500 text-white rounded-md text-sm hover:bg-blue-600 transition-colors"
                      >
                        Claim Reward
                      </button>
                    )}
                    
                    {reward.claimed && (
                      <div className="absolute inset-0 bg-white bg-opacity-50 flex items-center justify-center rounded-lg">
                        <span className="text-sm font-medium text-gray-500">Claimed</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default BattlePass;